import type { StoryblokClient } from '@storyblok/js';
import { apiPlugin, storyblokInit } from '@storyblok/js';

import { fallbackLanguage, languages } from '@valk-nx/core/lib/core';
import type { Logger } from '@valk-nx/core/lib/logging/logger';
import { consoleLogger } from '@valk-nx/core/lib/logging/logger';
import type { StoryblokLink as StoryblokMultilink } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { isHomeUrl } from './home-url.helper';
import { StoryblokHelper } from './storyblok.helper';

export interface SiteMapEntry {
	loc: string;
	changefreq: string;
	priority: number;
	last_mod: undefined;
}

export interface StoryblokLink {
	real_path: string;
	alternates: { lang: string; path: string }[];
}
export interface StoryblokItems {
	items: { item: { cached_url: string } }[];
}

export interface ExcludeEntry {
	url: string;
	isExternal: boolean;
}

/* v8 ignore start */
export async function getSitemap(
	sbToken: string,
	domain: string,
	logger: Logger = consoleLogger,
): Promise<SiteMapEntry[]> {
	try {
		const storyblokApi = storyblokInit({
			accessToken: sbToken,
			apiOptions: {
				cache: { clear: 'auto', type: 'none' },
			},
			use: [apiPlugin],
		}).storyblokApi;

		if (storyblokApi) {
			// NOTE: In environments like AWS App Runner, the Node.js process may stay "warm" across requests,
			// causing a cached instance of `storyblokApi` or its internal results to persist unexpectedly.
			// Even when using `cache: { type: 'none' }`, the SDK may internally cache unless explicitly flushed.
			// This helps prevent stale data in long-lived serverless containers.
			await storyblokApi.flushCache();

			const excludeUrls = await getExcludeList(storyblokApi, domain);

			const links = (
				await storyblokApi.getAll('cdn/links', {
					language: 'nl',
					per_page: 1000,
					resolve_links: 'link',
					version: 'published',
				})
			).filter(
				(link) =>
					!link.slug.startsWith('global') &&
					link.slug !== 'error-404' &&
					link.slug !== 'redirects',
			);

			const allSitemapData = parseSitemapData(links, domain);

			const filteredSitemapData = new Set();
			const sitemapData = allSitemapData.filter((obj) => {
				if (filteredSitemapData.has(obj.loc)) {
					return false;
				} else {
					filteredSitemapData.add(obj.loc);
					return true;
				}
			});

			return filterExcludedUrls(sitemapData, excludeUrls);
		}
		return [];
	} catch (error) {
		logger.error({ err: error, domain }, 'Failed to build sitemap');
		return [];
	}
}
/* v8 ignore stop */

export async function getExcludeList(
	api: StoryblokClient,
	domain: string,
): Promise<ExcludeEntry[]> {
	const global = await api.get('cdn/stories/globals', {
		language: fallbackLanguage,
		resolve_links: 'link',
		version: 'published',
	});
	const { story, links } = global.data;

	// filter before resolving languages
	const config = story.content.content.filter(
		(item: { component: string }) => item.component === 'sitemapConfig',
	);

	return languages.flatMap((language) =>
		getCachedUrls(
			config.map(
				(component: { items: { item: StoryblokMultilink }[] }) => ({
					...component,
					items: component.items.map((entry) => ({
						...entry,
						item: StoryblokHelper.getTranslatedUrl(
							entry.item,
							links,
							language,
						),
					})),
				}),
			),
			domain,
		),
	);
}

export function filterExcludedUrls(
	sitemapData: SiteMapEntry[],
	excludeUrls: ExcludeEntry[],
): SiteMapEntry[] {
	return sitemapData.filter(
		(data) =>
			!excludeUrls.some(
				(exclude) =>
					data.loc === exclude.url ||
					(!exclude.isExternal &&
						data.loc.startsWith(exclude.url + '/')),
			),
	);
}

export function getCachedUrls(
	inputArray: StoryblokItems[],
	domain: string,
): ExcludeEntry[] {
	if (!Array.isArray(inputArray) || inputArray.length === 0) {
		return [];
	}

	const cachedUrls = inputArray.flatMap((obj) =>
		obj.items.map(
			(item: { item: { cached_url: string } }): ExcludeEntry => {
				const url = item.item.cached_url;

				// NOTE: Do not add domain in cachedUrl when its already in the cached_url
				if (url.startsWith(domain)) {
					return {
						url: removeTrailingSlash(url),
						isExternal: true,
					};
				}
				const route = removeTrailingSlash(url).startsWith('/')
					? removeTrailingSlash(url)
					: `/${removeTrailingSlash(url)}`;

				return {
					url: `${domain}${route}`,
					isExternal: false,
				};
			},
		),
	);

	return cachedUrls;
}

export function parseSitemapData(
	links: StoryblokLink[],
	domain: string,
): SiteMapEntry[] {
	return links.flatMap((result) => {
		const baseConfig = {
			changefreq: 'weekly',
			priority: 0.5,
			last_mod: undefined,
		};
		const isHome = isHomeUrl(result.real_path);
		const path = isHome ? '' : removeTrailingSlash(result.real_path);
		const baseData = {
			loc: `${domain}${path}`,
			...baseConfig,
		};

		const alternates = result.alternates.map((alt) => {
			const altPath = isHome ? '' : `/${removeTrailingSlash(alt.path)}`;
			return {
				loc: `${domain}/${alt.lang}${altPath}`,
				...baseConfig,
			};
		});

		return [...alternates, baseData];
	});
}

export function removeTrailingSlash(value: string) {
	return value?.endsWith('/') ? value.slice(0, -1) : value;
}

export function removeParameters(value: string) {
	const [url] = value.split('?');

	return url;
}
